import {
  isSectionStructureObject,
  isSubSectionStructureObject,
  isWidgetStructureObject,
  ModuleStructureObject,
  SectionStructureObject,
  SubSectionStructureObject,
  WidgetStructureObject,
} from '../../api/interfaces';
import {
  multiplierWidgetWeight,
  type MultiplierOption,
} from '../../components/new-elements/MultiplierRadioButton/multiplier-answer';

/** Map of `widgetId` to the computed total weight for that widget. */
export type WidgetTotalWeightsMap = Record<string, number>;

/** Map of `subsectionId` to the list of child `widgetId`s. */
export type SubsectionWidgetsMap = Record<string, string[]>;

/** Map of `subsectionId` to configured points (may be undefined). */
export type SubsectionPointsMap = Record<string, number | undefined>;

/** Map of `sectionId` to configured points (may be undefined). */
export type SectionsPointsMap = Record<string, number | undefined>;

/** Map of `sectionId` to the sum of points used by its subsections. */
export type SectionUsedPointsMap = Record<string, number>;
export type SubsectionWeightsMap = Record<
  string,
  { points: number | undefined; totalWeight: number }
>;

/**
 * Calculate a map of widget total weights from the module structure.
 *
 * The function inspects widget customization to extract `weight` for simple
 * widgets or the maximum option weight for choice widgets (Radio/Combo).
 *
 * @param structure Module structure object (may be null/undefined)
 * @returns Map where keys are widget ids and values are numeric weights (>=0)
 */
export const calculateWidgetTotalWeights = (
  structure: ModuleStructureObject | null | undefined,
): WidgetTotalWeightsMap => {
  if (!structure) return {};

  return Object.fromEntries(
    Object.entries(structure)
      .filter((x): x is [string, WidgetStructureObject<unknown>] =>
        isWidgetStructureObject(x[1]),
      )
      .map(([id, node]) => {
        if (node._mappedType === 'TextBox' || node._mappedType === 'CheckBox') {
          const weight = (node.customization as { weight?: number }).weight;
          return [id, weight ?? 0] as const;
        }

        if (node._mappedType === 'MultiRadioButton') {
          // The widget weight is the configured cap, NOT the sum of each
          // group's max option weight.
          const cap = (node.customization as { cap?: number }).cap;
          return [id, cap ?? 0] as const;
        }

        if (node._mappedType === 'MultiplierRadioButton') {
          // The best reachable score: multiplier × max over the non-default
          // options. The default option is worth nothing.
          const options = (
            node.customization as { options?: MultiplierOption[] }
          ).options;
          return [id, multiplierWidgetWeight(options)] as const;
        }

        if (
          node._mappedType === 'RadioButton' ||
          node._mappedType === 'ComboBox'
        ) {
          const options = (
            node.customization as {
              options?: { label: string; value: string; weight?: number }[];
            }
          ).options;
          const totalWeight = options
            ? options.reduceRight(
                (acc, option) =>
                  acc > (option.weight ?? 0) ? acc : (option.weight ?? 0),
                0,
              )
            : 0;
          return [id, totalWeight] as const;
        }

        return [id, 0] as const;
      }),
  );
};

export const calculateSubsectionWidgets = (
  structure: ModuleStructureObject | null | undefined,
): SubsectionWidgetsMap => {
  if (!structure) return {};

  return Object.fromEntries(
    Object.entries(structure)
      .filter((x): x is [string, SubSectionStructureObject] =>
        isSubSectionStructureObject(x[1]),
      )
      .map(([id, subSection]) => {
        return [id, subSection.children];
      }),
  );
};

/**
 * Build a map of subsection id -> widget id[] from the module structure.
 *
 * @param structure Module structure object (may be null/undefined)
 * @returns Map where keys are subsection ids and values are arrays of widget ids
 */

export const calculateSectionsPoints = (
  structure: ModuleStructureObject | null | undefined,
): SectionsPointsMap => {
  if (!structure) return {};

  return Object.fromEntries(
    Object.entries(structure)
      .filter((x): x is [string, SectionStructureObject] =>
        isSectionStructureObject(x[1]),
      )
      .map(([id, section]) => {
        return [id, section.customization.points] as const;
      }),
  );
};

/**
 * Extract configured points for each section in the module structure.
 *
 * @param structure Module structure object (may be null/undefined)
 * @returns Map of sectionId -> configured points (may be undefined)
 */

export const calculateSectionUsedPoints = (
  structure: ModuleStructureObject | null | undefined,
): SectionUsedPointsMap => {
  if (!structure) return {};

  return Object.fromEntries(
    Object.entries(structure)
      .filter((x): x is [string, SectionStructureObject] =>
        isSectionStructureObject(x[1]),
      )
      .map(([sectionId, section]) => {
        const usedPoints = section.children.reduce((acc, subSectionId) => {
          const subSection = structure[
            subSectionId
          ] as SubSectionStructureObject;
          return acc + (subSection.customization.points ?? 0);
        }, 0);
        return [sectionId, usedPoints] as const;
      }),
  );
};

/**
 * Calculate how many points are allocated (used) inside each section by
 * summing its subsections' configured points.
 *
 * @param structure Module structure object (may be null/undefined)
 * @returns Map of sectionId -> used points (number)
 */

export const calculateSubsectionsPoints = (
  structure: ModuleStructureObject | null | undefined,
): SubsectionPointsMap => {
  if (!structure) return {};

  return Object.fromEntries(
    Object.entries(structure)
      .filter((x): x is [string, SubSectionStructureObject] =>
        isSubSectionStructureObject(x[1]),
      )
      .map(([id, subsection]) => {
        return [id, subsection.customization.points] as const;
      }),
  );
};

/**
 * Extract configured points for each subsection in the module structure.
 *
 * @param structure Module structure object (may be null/undefined)
 * @returns Map of subsectionId -> configured points (may be undefined)
 */

export const calculateSubsectionWeights = (
  subsectionWidgets: SubsectionWidgetsMap,
  widgetTotalWeights: WidgetTotalWeightsMap,
  subsectionPoints: SubsectionPointsMap,
): SubsectionWeightsMap => {
  return Object.fromEntries(
    Object.entries(subsectionWidgets).map(([sectionId, widgetIds]) => {
      const totalWeight = widgetIds.reduce((acc, widgetId) => {
        return acc + (widgetTotalWeights[widgetId] ?? 0);
      }, 0);
      const points = subsectionPoints[sectionId];
      return [sectionId, { points, totalWeight }] as const;
    }),
  );
};

/**
 * Compute subsection totals: configured points and total widget weight.
 *
 * @param subsectionWidgets Map of subsectionId -> widget ids
 * @param widgetTotalWeights Map of widgetId -> weight
 * @param subsectionPoints Map of subsectionId -> configured points
 * @returns Map of subsectionId -> { points, totalWeight }
 */

export const calculateMaxPointsForWidget = (
  widgetId: string,
  subsectionWidgets: SubsectionWidgetsMap,
  widgetTotalWeights: WidgetTotalWeightsMap,
  subsectionWeights: SubsectionWeightsMap,
): number => {
  const sectionId = Object.entries(subsectionWidgets).find(([, widgets]) =>
    widgets.includes(widgetId),
  )?.[0];
  if (!sectionId) return 0;

  const widgetTotalWeight = widgetTotalWeights[widgetId];
  if (!widgetTotalWeight || widgetTotalWeight === 0) return 0;

  const sectionData = subsectionWeights[sectionId];
  if (
    !sectionData ||
    sectionData.totalWeight === 0 ||
    sectionData.points === undefined
  )
    return 0;

  return (
    Math.round(
      ((sectionData.points * widgetTotalWeight) / sectionData.totalWeight) *
        100,
    ) / 100
  );
};

/**
 * Calculate the maximum points assignable to a widget based on its
 * weight relative to the total weight of its subsection and the subsection's
 * configured points.
 *
 * @param widgetId Widget id to evaluate
 * @param subsectionWidgets Map of subsectionId -> widget ids
 * @param widgetTotalWeights Map of widgetId -> weight
 * @param subsectionWeights Map of subsectionId -> { points, totalWeight }
 * @returns Numeric max points for the widget (rounded to 2 decimals)
 */

export const calculateWeightedPointsForWidget = (
  widgetId: string,
  weight: number | undefined | null,
  subsectionWidgets: SubsectionWidgetsMap,
  subsectionWeights: SubsectionWeightsMap,
): number => {
  if (!weight) return 0;

  const sectionId = Object.entries(subsectionWidgets).find(([, widgets]) =>
    widgets.includes(widgetId),
  )?.[0];
  if (!sectionId) return 0;

  const sectionData = subsectionWeights[sectionId];
  if (
    !sectionData ||
    sectionData.totalWeight === 0 ||
    sectionData.points === undefined
  )
    return 0;

  return (
    Math.round(
      ((sectionData.points * weight) / sectionData.totalWeight) * 100,
    ) / 100
  );
};
